import { useCallback } from 'react';
import { useVisualEffects } from './useVisualEffects';
import type { StateType } from '@/types/visualEffects';

/**
 * State duration presets in milliseconds.
 */
export const STATE_DURATIONS: Record<StateType, number> = {
  celebration: 4000,
  gratitude: 3000,
  contemplation: 5000,
  connection: 3000,
  reflection: 4000,
  none: 0,
};

/**
 * Hook for triggering visual effect state overlays.
 *
 * @example
 * ```tsx
 * const { celebrate, showGratitude } = useEffectState();
 *
 * // On check-in complete
 * celebrate();
 *
 * // On donation
 * showGratitude();
 * ```
 */
export function useEffectState() {
  const { triggerState, clearState, temporaryState } = useVisualEffects();

  const celebrate = useCallback(() => {
    triggerState('celebration', STATE_DURATIONS.celebration);
  }, [triggerState]);

  const showGratitude = useCallback(() => {
    triggerState('gratitude', STATE_DURATIONS.gratitude);
  }, [triggerState]);

  const showContemplation = useCallback(() => {
    triggerState('contemplation', STATE_DURATIONS.contemplation);
  }, [triggerState]);

  const showConnection = useCallback(() => {
    triggerState('connection', STATE_DURATIONS.connection);
  }, [triggerState]);

  const showReflection = useCallback(() => {
    triggerState('reflection', STATE_DURATIONS.reflection);
  }, [triggerState]);

  const clear = useCallback(() => {
    clearState();
  }, [clearState]);

  const isStateActive = useCallback(
    (state: StateType): boolean => {
      return temporaryState === state;
    },
    [temporaryState]
  );

  return {
    celebrate,
    showGratitude,
    showContemplation,
    showConnection,
    showReflection,
    clear,
    isStateActive,
    currentState: temporaryState,
  };
}
